import React, { useReducer, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import AuthenticationContext from '../contexts/AuthenticationContext';

const AuthenticationProvider = ({ instance, children }) => {
  const { i18n } = useTranslation();

  const [ignored, forceUpdate] = useReducer((x) => x + 1, 0);

  const [preLoginHandlers, setPreLoginHandlers] = useState([]);

  function login() {
    if (preLoginHandlers.length) {
      Promise.all(preLoginHandlers.map((handler) => Promise.resolve(handler()))).then(() => {
        console.log('login redirect promises fullfilled.');
        loginRedirect();
      });
    } else {
      loginRedirect();
    }
  }

  function loginRedirect() {
    instance
      .loginRedirect({
        extraQueryParameters: {
          lang: i18n.language
        }
      })
      .catch((error) => console.log(error));
  }

  function logout() {
    instance.logoutRedirect().catch((error) => console.log(error));
  }

  function account() {
    const accounts = instance.getAllAccounts();

    if (accounts.length !== 0) {
      return accounts[0];
    }

    return null;
  }

  function isAuthenticated() {
    return instance.getAllAccounts().length > 0;
  }

  function hasExpired(account) {
    return account.idTokenClaims.exp < Math.floor(Date.now() / 1000);
  }
  function hasRole(role) {
    const accounts = instance.getAllAccounts();

    if (accounts.length > 0) {
      if (typeof accounts[0].idTokenClaims.role !== 'undefined') {
        return accounts[0].idTokenClaims.role.includes(role);
      }
    }

    return false;
  }

  function addPreLoginHandler(handler) {
    let handlers = preLoginHandlers;
    handlers.push(handler);
    setPreLoginHandlers(handlers);
  }

  function renewToken() {
    instance
      .acquireTokenSilent({ account: account() })
      .then((response) => forceUpdate())
      .catch(async (error) => {
        console.log(error);

        if (error instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect({ account: account() });
        }
      });
  }

  async function callWithToken(call) {
    return instance
      .acquireTokenSilent({ account: account() })
      .then((response) => {
        if (response) {
          return call(response.idToken);
        }
      })
      .catch(async (error) => {
        console.log(error);

        if (error instanceof InteractionRequiredAuthError) {
          return instance.acquireTokenRedirect({ account: account() });
        }
      });
  }

  useEffect(() => {
    instance
      .handleRedirectPromise()
      .then((response) => {
        if (response) {
          // Returning from a view/edit profile redirect.
          if (response.idTokenClaims['acr'] === 'b2c_1a_generalprofileedit') {
            logout();
          }

          // Returning from a forgotten password redirect.
          if (response.idTokenClaims['acr'] === 'b2c_1a_forgottenpassword') {
            logout();
          }
        } else {
          // Logout authenticated user account if expired.
          if (isAuthenticated() && hasExpired(account())) {
            logout();
          }
        }

        // Force rerender to update account information.
        forceUpdate();
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  return (
    <AuthenticationContext.Provider
      value={{
        instance,
        account,
        isAuthenticated,
        login,
        logout,
        renewToken,
        callWithToken,
        hasRole,
        addPreLoginHandler
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

export default AuthenticationProvider;
